import apiClient from "../../apis/api-client";
import { toast } from "react-toastify";
import FileSaver from "file-saver";
import {
  BaseProcedureEntity,
  ProcedureDetailType,
} from "../../domain/procedures/procedure-entity";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../domain/pagination/paging";
import { rebuildQuery } from "../../../utils/url-utils";
import {
  reportAsyncError,
  reportUnexpectedError,
  BaseApplicationService,
  SmartApplyApiError,
  generateZip,
} from "@graffer-inc/smart-apply";
import { ProcedureImportStatusType } from "../../domain/procedures/procedure-import-status";
import { PublishStatus } from "@graffer-inc/cloud-gov-core";
import {
  downloadCSVForUpdateGroup,
  bulkUpdateGroupPreview,
  bulkUpdateGroup,
  UpdateProcedureGroupPreviewResponse,
  UpdateProcedureGroupResponse,
} from "../../domain/procedures/procedure-update-group";
import { ProceduresService } from "../smart-apply/procedures/procedures-service";

export type OrganizationProceduresServiceProps = {
  isLoading: boolean;
  isBulkDownloading: boolean;
  procedures: Array<BaseProcedureEntity>;
  paging: {
    current: number;
    total: number;
  };
  selectedStatus: PublishStatus[];
  selectedGroupIds: string[];
  selectedProcedureIds: Array<string>;
  queryName: string;
  setPage: typeof OrganizationProceduresService.prototype.setPage;
  movePage: typeof OrganizationProceduresService.prototype.movePage;
  setName: typeof OrganizationProceduresService.prototype.setName;
  setSelectedStatus: typeof OrganizationProceduresService.prototype.setSelectedStatus;
  setSelectedGroupIds: typeof OrganizationProceduresService.prototype.setSelectedGroupIds;
  fetchProceduresByOrgId: typeof OrganizationProceduresService.prototype.fetchProceduresByOrgId;
  searchProcedures: typeof OrganizationProceduresService.prototype.searchProcedures;
  createProcedure: typeof OrganizationProceduresService.prototype.createProcedure;
  importProcedureFromFiles: typeof OrganizationProceduresService.prototype.importProcedureFromFiles;
  checkProcedureImportFile: typeof OrganizationProceduresService.prototype.checkProcedureImportFile;
  onBulkExport: typeof OrganizationProceduresService.prototype.onBulkExport;
  selectToggle: typeof OrganizationProceduresService.prototype.selectToggle;
  selectAll: typeof OrganizationProceduresService.prototype.selectAll;
  isAllSelected: typeof OrganizationProceduresService.prototype.isAllSelected;
  onDownloadCsvForUpdateGroup: typeof ProceduresService.prototype.onDownloadCSVForUpdateGroup;
  bulkUpdateGroupPreview: typeof ProceduresService.prototype.bulkUpdateGroupPreview;
  bulkUpdateGroup: typeof ProceduresService.prototype.bulkUpdateGroup;
};

export class OrganizationProceduresService extends BaseApplicationService<OrganizationProceduresServiceProps> {
  private readonly procedureCountPerPage: number = DefaultNumItemsPerPages;

  state: OrganizationProceduresServiceProps = {
    isLoading: false,
    isBulkDownloading: false,
    procedures: [],
    paging: {
      current: 1,
      total: 0,
    },
    queryName: "",
    selectedStatus: [],
    selectedGroupIds: [],
    selectedProcedureIds: [],
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setName: this.setName.bind(this),
    setSelectedStatus: this.setSelectedStatus.bind(this),
    setSelectedGroupIds: this.setSelectedGroupIds.bind(this),
    fetchProceduresByOrgId: this.fetchProceduresByOrgId.bind(this),
    searchProcedures: this.searchProcedures.bind(this),
    createProcedure: this.createProcedure.bind(this),
    importProcedureFromFiles: this.importProcedureFromFiles.bind(this),
    checkProcedureImportFile: this.checkProcedureImportFile.bind(this),
    onBulkExport: this.onBulkExport.bind(this),
    selectToggle: this.selectToggle.bind(this),
    selectAll: this.selectAll.bind(this),
    isAllSelected: this.isAllSelected.bind(this),
    onDownloadCsvForUpdateGroup: this.onDownloadCSVForUpdateGroup.bind(this),
    bulkUpdateGroupPreview: this.bulkUpdateGroupPreview.bind(this),
    bulkUpdateGroup: this.bulkUpdateGroup.bind(this),
  };

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setName(name: string) {
    this.setState({
      queryName: name,
    });
  }

  setSelectedStatus(status: PublishStatus[]) {
    this.setState({
      selectedStatus: status,
    });
  }

  setSelectedGroupIds(groupIds: string[]) {
    this.setState({
      selectedGroupIds: groupIds,
    });
  }

  searchProcedures() {
    this.history.replace(
      rebuildQuery({
        page: "1",
        name: this.state.queryName,
        groupIds: this.state.selectedGroupIds.join(","),
      }),
    );
  }

  async fetchProceduresByOrgId(orgId: string) {
    this.setIsLoading(true);
    try {
      const { data: procedures, headers } =
        await apiClient.fetchProceduresByOrgId(
          orgId,
          this.state.paging.current,
          this.procedureCountPerPage,
          {
            name:
              this.state.queryName.length > 0
                ? this.state.queryName
                : undefined,
            publishStatus:
              this.state.selectedStatus.length > 0
                ? this.state.selectedStatus
                : undefined,
            groupIds:
              this.state.selectedGroupIds.length > 0
                ? this.state.selectedGroupIds
                : undefined,
          },
        );
      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.procedureCountPerPage,
          ),
        },
        procedures: procedures || [],
        selectedProcedureIds: [],
      });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続きの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async createProcedure(orgId: string, detail: ProcedureDetailType) {
    try {
      const { data: proc } = await apiClient.createProcedure({
        ...detail,
        orgId,
        isGrafferManaged: true,
      });
      toast.success("新規手続きを作成しました。");
      return proc;
    } catch (e) {
      reportAsyncError(e);
      toast.error("手続き作成に失敗しました。");
      return;
    }
  }

  async checkProcedureImportFile(
    jsonData: File,
    orgId: string,
  ): Promise<ProcedureImportStatusType | void> {
    try {
      const { data } = await apiClient.checkProcedureImportFile(
        jsonData,
        orgId,
      );
      return data;
    } catch (e) {
      reportAsyncError(e);
      toast.error("インポート用のファイルチェックに失敗しました。");
    }
  }

  async importProcedureFromFiles(
    jsonFiles: File[],
    orgId: string,
    groupId: string,
    customPathClearTargets: string[],
  ): Promise<{
    importedProcIds: string[];
    errorMessages: string[];
  }> {
    const errorMessages: string[] = [];
    const promises = jsonFiles.map(async (jsonFile) => {
      try {
        // FIXME:: to bulk import
        const { data } = await apiClient.importProcedureFromFile(
          jsonFile,
          orgId,
          groupId,
          customPathClearTargets.includes(jsonFile.name),
        );
        return data;
      } catch (e) {
        if (SmartApplyApiError.isErrorCode(e, "custom_url_already_exists")) {
          errorMessages.push(
            `URLが重複するため、登録できませんでした。[${jsonFile.name}]`,
          );
          return;
        }
        reportAsyncError(e);
        errorMessages.push(`インポートに失敗しました。[${jsonFile.name}]`);
        return;
      }
    });
    const result = await Promise.all(promises);
    const importedProcIds = result.filter((p) => !!p).map((p) => p!.id);
    return {
      importedProcIds,
      errorMessages,
    };
  }

  private setIsLoading(isLoading: boolean) {
    this.setState({
      isLoading: isLoading,
    });
  }

  async onBulkExport() {
    this.setState({ isBulkDownloading: true });
    try {
      const promises = this.state.selectedProcedureIds.map(async (id) => {
        const { data } = await apiClient.exportProcedureSettings(id);
        const blob = new Blob([JSON.stringify(data)], {
          type: "application/json",
        });
        const proc = this.state.procedures.find((p) => p.id === id);
        return {
          name: `${id}_${proc!.name}.json`,
          blob: blob as Blob,
        };
      });
      const files = await Promise.all(promises);
      const zip = await generateZip(files);
      FileSaver.saveAs(zip, `export_procedures.zip`);
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return;
      }
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      reportUnexpectedError(e);
    } finally {
      this.setState({ isBulkDownloading: false });
    }
  }

  isAllSelected(): boolean {
    if (this.state.procedures.length === 0) return false;
    const procedureIds = this.state.procedures.map((p) => p.id as string);
    const selectedProcedureIds = this.state.selectedProcedureIds;
    return procedureIds.every((v) => selectedProcedureIds.includes(v));
  }

  selectToggle(id: string) {
    const procedureIds = [...this.state.selectedProcedureIds];
    const found = this.state.procedures.find((p) => p.id === id);
    if (!found) {
      throw new Error(`Illegal select procedure`);
    }

    this.setState({
      selectedProcedureIds: procedureIds.find((pid) => pid === id)
        ? procedureIds.filter((pid) => pid !== id)
        : [...procedureIds, id],
    });
  }

  selectAll() {
    if (this.isAllSelected()) {
      this.setState({
        selectedProcedureIds: [],
      });
    } else {
      this.setState({
        selectedProcedureIds: [...this.state.procedures.map((p) => p.id)],
      });
    }
  }

  async onDownloadCSVForUpdateGroup(
    orgId: string,
    groupIds: string[],
  ): Promise<void | string> {
    return downloadCSVForUpdateGroup(orgId, groupIds);
  }

  async bulkUpdateGroupPreview(
    csv: string,
    orgId: string,
  ): Promise<UpdateProcedureGroupPreviewResponse | string> {
    return bulkUpdateGroupPreview(csv, orgId);
  }

  async bulkUpdateGroup(
    csv: string,
    orgId: string,
  ): Promise<UpdateProcedureGroupResponse | string> {
    return bulkUpdateGroup(csv, orgId);
  }
}
